import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Package, MapPin, Clock, DollarSign, ArrowLeft, ArrowRight,
  CheckCircle, Truck, User, Phone, Mail, FileText, AlertCircle,
  Zap, Shield, Star
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import api from '../services/api';
import { toast } from 'sonner';

const CourierBookingPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [priceEstimate, setPriceEstimate] = useState(null);
  
  const [formData, setFormData] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    pickupAddress: '',
    pickupCity: '',
    pickupZip: '',
    pickupInstructions: '',
    deliveryAddress: '',
    deliveryCity: '',
    deliveryZip: '',
    deliveryInstructions: '',
    recipientName: '',
    recipientPhone: '',
    packageType: 'small',
    packageDescription: '',
    packageWeight: '',
    isFragile: false,
    pickupDate: '',
    pickupTime: '',
    isUrgent: false
  });

  const packageTypes = [
    { id: 'documents', label: 'Documents', description: 'Papers, contracts, files', icon: '📄', price: '$15' },
    { id: 'small', label: 'Small Package', description: 'Envelope, small box (up to 5 lbs)', icon: '📦', price: '$20' },
    { id: 'medium', label: 'Medium Package', description: 'Shoebox size (5-20 lbs)', icon: '📦', price: '$30' },
    { id: 'large', label: 'Large Package', description: 'Larger items (20-50 lbs)', icon: '📦', price: '$45' }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const calculateEstimate = async () => {
    setLoading(true);
    try {
      const response = await api.post('/pricing/estimate', {
        service_type: 'courier',
        pickup_address: `${formData.pickupAddress}, ${formData.pickupCity}, NC ${formData.pickupZip}`,
        delivery_address: `${formData.deliveryAddress}, ${formData.deliveryCity}, NC ${formData.deliveryZip}`,
        package_type: formData.packageType,
        is_urgent: formData.isUrgent
      });
      setPriceEstimate(response.data);
      setStep(4);
    } catch (error) {
      const basePrice = formData.packageType === 'documents' ? 15 : 
                        formData.packageType === 'small' ? 20 :
                        formData.packageType === 'medium' ? 30 : 45;
      const urgentFee = formData.isUrgent ? 15 : 0;
      setPriceEstimate({
        base_fare: basePrice,
        urgent_fee: urgentFee,
        total: basePrice + urgentFee,
        estimated_time: formData.isUrgent ? '30-60 mins' : '1-2 hours'
      });
      setStep(4);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await api.post('/checkout/guest/initiate', {
        service_type: 'courier',
        service_name: 'Package Delivery',
        customer_name: formData.customerName,
        customer_email: formData.customerEmail,
        customer_phone: formData.customerPhone,
        address: formData.pickupAddress,
        city: formData.pickupCity,
        state: 'NC',
        zip_code: formData.pickupZip,
        scheduled_date: formData.pickupDate,
        scheduled_time: formData.pickupTime,
        special_instructions: `
PACKAGE TYPE: ${formData.packageType}
PACKAGE DESCRIPTION: ${formData.packageDescription}
${formData.isFragile ? 'FRAGILE - Handle with care' : ''}
${formData.isUrgent ? 'URGENT DELIVERY' : ''}

PICKUP: ${formData.pickupAddress}, ${formData.pickupCity}, NC ${formData.pickupZip}
${formData.pickupInstructions ? 'Pickup Notes: ' + formData.pickupInstructions : ''}

DELIVERY TO: ${formData.deliveryAddress}, ${formData.deliveryCity}, NC ${formData.deliveryZip}
RECIPIENT: ${formData.recipientName} - ${formData.recipientPhone}
${formData.deliveryInstructions ? 'Delivery Notes: ' + formData.deliveryInstructions : ''}
        `.trim(),
        total_amount: priceEstimate?.total || 25,
        payment_method: 'card'
      });

      if (response.data?.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else if (response.data?.pending_id) {
        navigate(`/guest-checkout?pending=${response.data.pending_id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { num: 1, label: 'Your Info', icon: User },
    { num: 2, label: 'Locations', icon: MapPin },
    { num: 3, label: 'Package', icon: Package },
    { num: 4, label: 'Confirm', icon: CheckCircle }
  ];

  return (
    <div className="min-h-screen service-bg-courier relative overflow-hidden">
      {/* Animated Background Glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-cyan-500/15 rounded-full blur-[150px] animate-pulse" />
        <div className="absolute bottom-1/3 right-1/4 w-[400px] h-[400px] bg-blue-500/10 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '1s' }} />
      </div>
      
      {/* Header */}
      <div className="sticky-header px-4 py-4">
        <div className="max-w-lg mx-auto flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)} 
            className="w-10 h-10 rounded-xl glass-card flex items-center justify-center text-slate-400 hover:text-white transition-colors"
            data-testid="courier-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="w-12 h-12 rounded-2xl icon-gradient-courier flex items-center justify-center shadow-lg glow-courier">
            <Truck className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white font-['Plus_Jakarta_Sans']">Courier</h1>
            <h1 className="text-xl font-bold text-white font-['Plus_Jakarta_Sans']">Service</h1>
          </div>
        </div>
      </div>

      {/* Premium Service Banner */}
      <div className="max-w-lg mx-auto px-4 mt-4 mb-6">
        <div className="glass-card-subtle p-4 flex items-center gap-3 border-cyan-500/20">
          <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
            <Zap className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <span className="text-cyan-400 font-semibold">Fast Delivery</span>
            <span className="text-slate-400 ml-2">— Same-day local delivery within 40 miles</span>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="max-w-lg mx-auto px-4 py-4">
        <div className="relative flex items-center justify-between">
          {/* Connecting Line */}
          <div className="step-connector" />
          <div 
            className="step-connector-active" 
            style={{ width: `${((step - 1) / (steps.length - 1)) * 100}%`, background: 'linear-gradient(90deg, #06B6D4, #3B82F6)' }} 
          />
          
          {steps.map((s, idx) => (
            <motion.div 
              key={s.num}
              className="flex flex-col items-center relative z-10"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: idx * 0.1 }}
            >
              <div 
                className={`step-node ${
                  step > s.num ? 'step-node-completed' : 
                  step === s.num ? 'bg-gradient-to-br from-cyan-500 to-blue-500 text-white shadow-lg shadow-cyan-500/40' : 'step-node-pending'
                }`}
                data-testid={`courier-step-${s.num}`}
              >
                {step > s.num ? <CheckCircle className="w-4 h-4" /> : <s.icon className="w-4 h-4" />}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="max-w-lg mx-auto px-4 pb-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.3 }}
            className="glass-card p-6 sm:p-8"
          >
            {/* Step 1: Customer Info */}
            {step === 1 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-2xl icon-gradient-courier flex items-center justify-center glow-courier">
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-100 font-['Plus_Jakarta_Sans']">Your Information</h2>
                    <p className="text-slate-400 text-sm">Tell us how to reach you</p>
                  </div>
                </div>
                
                <div className="space-y-5">
                  <div>
                    <label className="text-white/70 text-sm mb-2 block font-medium">Full Name *</label>
                    <Input
                      name="customerName"
                      value={formData.customerName}
                      onChange={handleChange}
                      placeholder="Your full name"
                      className="bg-white/5 border-white/10 text-white h-12 rounded-xl focus:border-[#3B82F6] focus:ring-[#3B82F6]/20 placeholder:text-white/50"
                      data-testid="customer-name-input"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-white/70 text-sm mb-2 block font-medium">Phone *</label>
                      <Input
                        name="customerPhone"
                        value={formData.customerPhone}
                        onChange={handleChange}
                        placeholder="(555) 123-4567"
                        className="bg-white/5 border-white/10 text-white h-12 rounded-xl focus:border-[#3B82F6] placeholder:text-white/50"
                        data-testid="customer-phone-input"
                      />
                    </div>
                    <div>
                      <label className="text-white/70 text-sm mb-2 block font-medium">Email *</label>
                      <Input
                        name="customerEmail"
                        type="email"
                        value={formData.customerEmail}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className="bg-white/5 border-white/10 text-white h-12 rounded-xl focus:border-[#3B82F6] placeholder:text-white/50"
                        data-testid="customer-email-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Trust Badges */}
                <div className="mt-8 pt-6 border-t border-white/10">
                  <div className="flex flex-wrap justify-center gap-6 text-white/40 text-xs">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-[#3B82F6]" />
                      <span>Secure & Private</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-[#06B6D4]" />
                      <span>Fast Processing</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-[#D4AF37]" />
                      <span>5-Star Service</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Locations */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#3B82F6]/20 to-[#06B6D4]/20 flex items-center justify-center border border-[#3B82F6]/30">
                    <MapPin className="w-6 h-6 text-[#3B82F6]" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white font-['Cormorant_Garamond']">Pickup & Delivery</h2>
                    <p className="text-white/50 text-sm">Where should we pick up and deliver?</p>
                  </div>
                </div>
                
                {/* Pickup Location */}
                <div className="relative">
                  <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#3B82F6] to-[#06B6D4]" />
                  
                  <div className="pl-14 pb-8 relative">
                    <div className="absolute left-4 top-0 w-4 h-4 rounded-full bg-[#3B82F6] border-4 border-[#030303] shadow-lg shadow-[#3B82F6]/50" />
                    <div className="bg-[#3B82F6]/10 border border-[#3B82F6]/30 rounded-2xl p-5">
                      <h3 className="text-[#3B82F6] font-semibold mb-4 flex items-center gap-2">
                        <span className="text-xs px-2 py-1 bg-[#3B82F6]/20 rounded-full">PICKUP</span>
                      </h3>
                      <div className="space-y-3">
                        <Input
                          name="pickupAddress"
                          value={formData.pickupAddress}
                          onChange={handleChange}
                          placeholder="Street Address"
                          className="bg-black/30 border-white/10 text-white h-11 rounded-xl placeholder:text-white/50"
                          data-testid="pickup-address-input"
                        />
                        <div className="grid grid-cols-2 gap-3">
                          <Input
                            name="pickupCity"
                            value={formData.pickupCity}
                            onChange={handleChange}
                            placeholder="City"
                            className="bg-black/30 border-white/10 text-white h-11 rounded-xl placeholder:text-white/50"
                          />
                          <Input
                            name="pickupZip"
                            value={formData.pickupZip}
                            onChange={handleChange}
                            placeholder="ZIP Code"
                            className="bg-black/30 border-white/10 text-white h-11 rounded-xl placeholder:text-white/50"
                          />
                        </div>
                        <Input
                          name="pickupInstructions"
                          value={formData.pickupInstructions}
                          onChange={handleChange}
                          placeholder="Pickup instructions (optional)"
                          className="bg-black/30 border-white/10 text-white h-11 rounded-xl placeholder:text-white/50"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Delivery Location */}
                  <div className="pl-14 relative">
                    <div className="absolute left-4 top-0 w-4 h-4 rounded-full bg-[#06B6D4] border-4 border-[#030303] shadow-lg shadow-[#06B6D4]/50" />
                    <div className="bg-[#06B6D4]/10 border border-[#06B6D4]/30 rounded-2xl p-5">
                      <h3 className="text-[#06B6D4] font-semibold mb-4 flex items-center gap-2">
                        <span className="text-xs px-2 py-1 bg-[#06B6D4]/20 rounded-full">DELIVERY</span>
                      </h3>
                      <div className="space-y-3">
                        <Input
                          name="deliveryAddress"
                          value={formData.deliveryAddress}
                          onChange={handleChange}
                          placeholder="Street Address"
                          className="bg-black/30 border-white/10 text-white h-11 rounded-xl placeholder:text-white/50"
                          data-testid="delivery-address-input"
                        />
                        <div className="grid grid-cols-2 gap-3">
                          <Input
                            name="deliveryCity"
                            value={formData.deliveryCity}
                            onChange={handleChange}
                            placeholder="City"
                            className="bg-black/30 border-white/10 text-white h-11 rounded-xl placeholder:text-white/50"
                          />
                          <Input
                            name="deliveryZip"
                            value={formData.deliveryZip}
                            onChange={handleChange}
                            placeholder="ZIP Code"
                            className="bg-black/30 border-white/10 text-white h-11 rounded-xl placeholder:text-white/50"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <Input
                            name="recipientName"
                            value={formData.recipientName}
                            onChange={handleChange}
                            placeholder="Recipient Name"
                            className="bg-black/30 border-white/10 text-white h-11 rounded-xl placeholder:text-white/50"
                            data-testid="recipient-name-input"
                          />
                          <Input
                            name="recipientPhone"
                            value={formData.recipientPhone}
                            onChange={handleChange}
                            placeholder="Recipient Phone"
                            className="bg-black/30 border-white/10 text-white h-11 rounded-xl placeholder:text-white/50"
                          />
                        </div>
                        <Input
                          name="deliveryInstructions"
                          value={formData.deliveryInstructions}
                          onChange={handleChange}
                          placeholder="Delivery instructions (optional)"
                          className="bg-black/30 border-white/10 text-white h-11 rounded-xl placeholder:text-white/50"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Package Details */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#3B82F6]/20 to-[#06B6D4]/20 flex items-center justify-center border border-[#3B82F6]/30">
                    <Package className="w-6 h-6 text-[#3B82F6]" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white font-['Cormorant_Garamond']">Package Details</h2>
                    <p className="text-white/50 text-sm">What are you sending?</p>
                  </div>
                </div>
                
                {/* Package Type Selection */}
                <div>
                  <label className="text-white/70 text-sm mb-3 block font-medium">Package Type *</label>
                  <div className="grid grid-cols-2 gap-3">
                    {packageTypes.map(type => (
                      <button
                        key={type.id}
                        onClick={() => setFormData(prev => ({ ...prev, packageType: type.id }))}
                        className={`p-4 rounded-2xl border text-left transition-all duration-300 group ${
                          formData.packageType === type.id
                            ? 'bg-gradient-to-br from-[#3B82F6]/20 to-[#06B6D4]/20 border-[#3B82F6] shadow-lg shadow-[#3B82F6]/20'
                            : 'bg-white/[0.02] border-white/10 hover:border-white/30 hover:bg-white/[0.05]'
                        }`}
                        data-testid={`package-type-${type.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <span className="text-2xl">{type.icon}</span>
                          <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                            formData.packageType === type.id ? 'bg-[#3B82F6] text-white' : 'bg-white/10 text-white/60'
                          }`}>
                            {type.price}
                          </span>
                        </div>
                        <p className={`font-semibold mt-2 ${formData.packageType === type.id ? 'text-white' : 'text-white/80'}`}>
                          {type.label}
                        </p>
                        <p className={`text-xs mt-1 ${formData.packageType === type.id ? 'text-white/70' : 'text-white/40'}`}>
                          {type.description}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="text-white/70 text-sm mb-2 block font-medium">Package Description</label>
                  <textarea
                    name="packageDescription"
                    value={formData.packageDescription}
                    onChange={handleChange}
                    placeholder="Describe what you're sending..."
                    rows={3}
                    className="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-white placeholder:text-white/50 focus:border-[#3B82F6] focus:outline-none resize-none"
                    data-testid="package-description-input"
                  />
                </div>

                {/* Options */}
                <div className="space-y-3">
                  <label className="flex items-center gap-4 p-4 bg-white/[0.02] border border-white/10 rounded-xl cursor-pointer hover:bg-white/[0.05] transition-colors group">
                    <input
                      type="checkbox"
                      name="isFragile"
                      checked={formData.isFragile}
                      onChange={handleChange}
                      className="w-5 h-5 rounded-md border-white/30 bg-transparent text-[#3B82F6] focus:ring-[#3B82F6]/20"
                      data-testid="fragile-checkbox"
                    />
                    <div className="flex-1">
                      <span className="text-white font-medium">Fragile Package</span>
                      <p className="text-white/40 text-xs mt-0.5">Handle with extra care</p>
                    </div>
                    <AlertCircle className="w-5 h-5 text-amber-400" />
                  </label>
                  
                  <label className="flex items-center gap-4 p-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl cursor-pointer hover:from-amber-500/15 hover:to-orange-500/15 transition-colors group">
                    <input
                      type="checkbox"
                      name="isUrgent"
                      checked={formData.isUrgent}
                      onChange={handleChange}
                      className="w-5 h-5 rounded-md border-amber-500/50 bg-transparent text-amber-500 focus:ring-amber-500/20"
                      data-testid="urgent-checkbox"
                    />
                    <div className="flex-1">
                      <span className="text-white font-medium">Urgent Delivery</span>
                      <p className="text-amber-400/70 text-xs mt-0.5">Priority handling, fastest route</p>
                    </div>
                    <span className="text-amber-400 font-bold text-sm">+$15</span>
                  </label>
                </div>

                {/* Schedule */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-white/70 text-sm mb-2 block font-medium">Pickup Date *</label>
                    <Input
                      type="date"
                      name="pickupDate"
                      value={formData.pickupDate}
                      onChange={handleChange}
                      min={new Date().toISOString().split('T')[0]}
                      className="bg-white/5 border-white/10 text-white h-12 rounded-xl [color-scheme:dark]"
                      data-testid="pickup-date-input"
                    />
                  </div>
                  <div>
                    <label className="text-white/70 text-sm mb-2 block font-medium">Pickup Time *</label>
                    <Input
                      type="time"
                      name="pickupTime"
                      value={formData.pickupTime}
                      onChange={handleChange}
                      className="bg-white/5 border-white/10 text-white h-12 rounded-xl [color-scheme:dark]"
                      data-testid="pickup-time-input"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Confirm */}
            {step === 4 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#3B82F6]/20 to-[#06B6D4]/20 flex items-center justify-center border border-[#3B82F6]/30">
                    <CheckCircle className="w-6 h-6 text-[#3B82F6]" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white font-['Cormorant_Garamond']">Confirm Delivery</h2>
                    <p className="text-white/50 text-sm">Review your order details</p>
                  </div>
                </div>
                
                {/* Price Summary Card */}
                <div className="relative overflow-hidden bg-gradient-to-br from-[#3B82F6]/20 via-[#06B6D4]/10 to-[#3B82F6]/20 border border-[#3B82F6]/30 rounded-2xl p-6">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-[#3B82F6]/20 rounded-full blur-3xl" />
                  <h3 className="text-lg font-bold text-white mb-4 relative z-10">Price Estimate</h3>
                  <div className="space-y-3 text-white/80 relative z-10">
                    <div className="flex justify-between items-center">
                      <span className="flex items-center gap-2">
                        <Package className="w-4 h-4 text-[#3B82F6]" />
                        Base Delivery Fee
                      </span>
                      <span className="font-semibold">${priceEstimate?.base_fare?.toFixed(2) || '20.00'}</span>
                    </div>
                    {formData.isUrgent && (
                      <div className="flex justify-between items-center text-amber-400">
                        <span className="flex items-center gap-2">
                          <Zap className="w-4 h-4" />
                          Urgent Delivery
                        </span>
                        <span className="font-semibold">+${priceEstimate?.urgent_fee?.toFixed(2) || '15.00'}</span>
                      </div>
                    )}
                    <div className="border-t border-white/20 pt-3 mt-3 flex justify-between items-center">
                      <span className="text-xl font-bold text-white">Total</span>
                      <span className="text-2xl font-bold bg-gradient-to-r from-[#3B82F6] to-[#06B6D4] bg-clip-text text-transparent">
                        ${priceEstimate?.total?.toFixed(2) || '35.00'}
                      </span>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-2 text-white/50 text-xs">
                    <Clock className="w-4 h-4" />
                    <span>Estimated delivery: {priceEstimate?.estimated_time || '1-2 hours'}</span>
                  </div>
                </div>

                {/* Order Summary */}
                <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-[#3B82F6]/20 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-[#3B82F6]" />
                    </div>
                    <div>
                      <p className="text-white/50 text-xs uppercase tracking-wider">Pickup</p>
                      <p className="text-white font-medium">{formData.pickupAddress}</p>
                      <p className="text-white/60 text-sm">{formData.pickupCity}, NC</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-[#06B6D4]/20 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-[#06B6D4]" />
                    </div>
                    <div>
                      <p className="text-white/50 text-xs uppercase tracking-wider">Delivery</p>
                      <p className="text-white font-medium">{formData.deliveryAddress}</p>
                      <p className="text-white/60 text-sm">{formData.deliveryCity}, NC • To: {formData.recipientName}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                      <Package className="w-5 h-5 text-white/70" />
                    </div>
                    <div>
                      <p className="text-white/50 text-xs uppercase tracking-wider">Package</p>
                      <p className="text-white font-medium capitalize">{formData.packageType} {formData.isFragile && '(Fragile)'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-white/70" />
                    </div>
                    <div>
                      <p className="text-white/50 text-xs uppercase tracking-wider">Pickup Time</p>
                      <p className="text-white font-medium">{formData.pickupDate} at {formData.pickupTime}</p>
                    </div>
                  </div>
                </div>

                <p className="text-white/40 text-sm text-center">
                  You'll be redirected to secure payment after confirming.
                </p>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-white/10">
              {step > 1 ? (
                <Button
                  variant="outline"
                  onClick={() => setStep(step - 1)}
                  className="border-white/20 text-white hover:bg-white/10 rounded-xl px-6"
                  data-testid="prev-step-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              ) : (
                <div />
              )}
              
              {step < 3 ? (
                <Button
                  onClick={() => setStep(step + 1)}
                  className="bg-gradient-to-r from-[#3B82F6] to-[#06B6D4] hover:from-[#2563EB] hover:to-[#0891B2] text-white rounded-xl px-6 shadow-lg shadow-[#3B82F6]/30"
                  disabled={
                    (step === 1 && (!formData.customerName || !formData.customerPhone || !formData.customerEmail)) ||
                    (step === 2 && (!formData.pickupAddress || !formData.deliveryAddress || !formData.recipientName || !formData.recipientPhone))
                  }
                  data-testid="next-step-btn"
                >
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : step === 3 ? (
                <Button
                  onClick={calculateEstimate}
                  className="bg-gradient-to-r from-[#3B82F6] to-[#06B6D4] hover:from-[#2563EB] hover:to-[#0891B2] text-white rounded-xl px-6 shadow-lg shadow-[#3B82F6]/30"
                  disabled={loading || !formData.pickupDate || !formData.pickupTime}
                  data-testid="get-price-btn"
                >
                  {loading ? 'Calculating...' : 'Get Price'}
                  <DollarSign className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  className="bg-gradient-to-r from-[#D4AF37] to-[#F4C2C2] hover:from-[#E6C965] hover:to-[#F4C2C2] text-black font-bold rounded-xl px-8 shadow-lg shadow-[#D4AF37]/30"
                  disabled={loading}
                  data-testid="book-pay-btn"
                >
                  {loading ? 'Processing...' : 'Book & Pay'}
                  <CheckCircle className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default CourierBookingPage;
